/* =========================================================
   Reports — data model: the column catalogue, the filter
   shape, and the row builders for each category.
   ========================================================= */
import type { ReactNode } from 'react';
import type { EstadoPdb, Lang } from '@/types';
import { ic, type IconElement } from '@/components/Icons';
import { COUNTRIES, ds } from '@/data';
import { instructorSchema, orgSchema, siteSchema } from '@/lib/fields';
import { progresoFicha } from '@/lib/progreso';
import { hayProgreso, progresoOrg, progresoSite } from '@/lib/progresoPortal';
import { CATALOGOS, SEP_LISTA, aLista } from '../../../db/columns';
import { fechaHoraOrdenable } from '@/lib/actualizado';

export type Category = 'orgs' | 'sites' | 'instructors' | 'consolidated';

export const CATS: Category[] = ['orgs', 'sites', 'instructors', 'consolidated'];

export const L = (lang: Lang, es: string, en: string) => (lang === 'es' ? es : en);

export function countryName(code: string | undefined, lang: Lang) {
  const c = COUNTRIES.find((x) => x.code === code);
  return c ? (lang === 'es' ? c.name_es : c.name_en) : code || '';
}

export const fullName = (a?: string, b?: string) => [a, b].filter(Boolean).join(' ');

export interface Comp {
  pct: number;
  /** Campos (y documentos) que faltan. */
  missing: number;
  pending: boolean;
  /** Los que están hechos y el total que cuenta. */
  done: number;
  total: number;
  /** La base aún no tiene porcentaje para esta ficha. */
  sinDato: boolean;
}

/** Rows carry the source record plus the derived `_*` helpers. */
export type ReportRow = Record<string, unknown> & { _key: string };

export interface ColDef {
  key: string;
  es: string;
  en: string;
  get: (r: ReportRow, lang: Lang) => string | number | undefined;
  cell?: (r: ReportRow, lang: Lang) => ReactNode;
  mono?: boolean;
  align?: 'left' | 'right' | 'center';
  /** Shown by default. */
  def?: boolean;
}

/* ---------- completeness per category ---------- */

/** El porcentaje de cada fila: EL MISMO que enseña el portal.
 *
 *  Organizaciones y sites leen el porcentaje GUARDADO en la base (tabla
 *  `progreso`), que es el del proceso de actualización:
 *
 *    · organización = su ficha + sus sites (con sus instructores) + sus
 *      documentos de compliance
 *    · site         = su ficha + sus instructores
 *
 *  Antes este informe medía solo la ficha suelta —los 27 campos de la
 *  organización, sin sus sites, sus instructores ni su compliance—, así
 *  que una organización con la ficha llena salía al 100 % aunque sus sites
 *  tuvieran decenas de pendientes. ETC lo detectó en octubre de 2026: el
 *  Excel no coincidía con el porcentaje real. Ahora no puede discrepar,
 *  porque no se calcula aquí: se lee.
 *
 *  El instructor no tiene porcentaje guardado propio: es su ficha, contada
 *  con la misma función que el servidor. */
export function recordCompleteness(category: Category, row: Record<string, unknown>): Comp {
  if (category === 'orgs') return deGuardado('organization', String(row.OrgId ?? ''));
  if (category === 'sites') return deGuardado('site', String(row.SiteId ?? ''));
  if (category === 'instructors') {
    const id = String(row.InstructorId ?? '');
    const p = progresoFicha('instructor', id, row, ds.marcasNA());
    return comp(p.hechos, p.total, p.pct);
  }
  // el cruzado no es una ficha, es una relación: no tiene porcentaje propio
  return { pct: 100, missing: 0, pending: false, done: 0, total: 0, sinDato: false };
}

function comp(hechos: number, total: number, pct: number): Comp {
  const faltan = total - hechos;
  return { pct, missing: faltan, pending: faltan > 0, done: hechos, total, sinDato: false };
}

function deGuardado(entidad: 'organization' | 'site', id: string): Comp {
  if (!hayProgreso(entidad, id)) return { pct: 0, missing: 0, pending: true, done: 0, total: 0, sinDato: true };
  const p = entidad === 'organization' ? progresoOrg(id) : progresoSite(id);
  return comp(p.hechos, p.total, p.pct);
}

/** Texto de la celda/exportación: «71%», o «sin dato» si la base aún no lo
 *  tiene (nunca un 0 % inventado). */
const pctTxt = (c: Comp, l: Lang) => (c.sinDato ? L(l, 'sin dato', 'no data') : c.pct + '%');
const numTxt = (c: Comp, n: number) => (c.sinDato ? '' : n);

/* ---------- consolidated cross-tab ---------- */

/** One row per Org → Site → Instructor relation. Organizations with no
 *  sites and sites with no instructors still appear, so gaps are visible. */
export function buildConsolidated(): ReportRow[] {
  const out: ReportRow[] = [];
  let n = 0;
  const px = (obj: Record<string, unknown>, prefix: string) => {
    const o: Record<string, unknown> = {};
    for (const k in obj) o[prefix + k] = obj[k];
    return o;
  };

  for (const org of ds.listOrgs()) {
    const orgBase = {
      ...px(org as unknown as Record<string, unknown>, 'org_'),
      _orgComp: recordCompleteness('orgs', org as unknown as Record<string, unknown>),
      _pdb: ds.getPdb(org.OrgId),
    };
    const sites = ds.listSites({ orgId: org.OrgId });
    if (sites.length === 0) {
      out.push({ _key: 'cx' + n++, ...orgBase });
      continue;
    }
    for (const site of sites) {
      const base = {
        ...orgBase,
        ...px(site as unknown as Record<string, unknown>, 'site_'),
        _siteComp: recordCompleteness('sites', site as unknown as Record<string, unknown>),
      };
      const asgs = ds.listAssignments({ siteId: site.SiteId });
      if (asgs.length === 0) {
        out.push({ _key: 'cx' + n++, ...base });
        continue;
      }
      for (const a of asgs) {
        const i = ds.getInstructor(a.InstructorId) ?? {};
        out.push({
          _key: 'cx' + n++,
          ...base,
          ...px(i as unknown as Record<string, unknown>, 'instr_'),
          PrimaryRoleId: a.PrimaryRoleId,
        });
      }
    }
  }
  return out;
}

/* ---------- column catalogue ---------- */

const s = (v: unknown) => (v == null ? '' : String(v));

/** Los códigos de un campo con catálogo, traducidos a etiquetas.
 *
 *  Separados por el mismo `;` que espera la importación, para que el informe
 *  se pueda volver a subir tal cual. */
const etiquetas = (campo: string, valor: unknown, l: Lang): string => {
  const cat = CATALOGOS[campo] ?? [];
  return aLista(valor)
    .map((c) => {
      const o = cat.find((x) => x.code === c);
      return o ? (l === 'es' ? o.label_es : o.label_en) : c;
    })
    .join(SEP_LISTA + ' ');
};

/** Los pendientes, en su propia columna.
 *
 *  Hubo también una columna «Completados» al lado; ETC la retiró en
 *  octubre de 2026: con el % Progreso y los pendientes ya se lee todo, y
 *  en el Excel era una columna más que nadie usaba. */
function cuentas(def: boolean, campo = '_comp', grupo = ''): ColDef[] {
  const c = (r: ReportRow) => r[campo] as Comp | undefined;
  return [
    { key: campo + '_pend', es: grupo + 'Pendientes', en: grupo + 'Pending', mono: true, align: 'center', def,
      get: (r) => (c(r) ? numTxt(c(r)!, c(r)!.missing) : '') },
  ];
}

/** El control PDB y quién/cuándo. Solo llega a ETC, que es quien ve
 *  Reportes. */
function pdbCols(grupo: string): ColDef[] {
  const e = (r: ReportRow) => r._pdb as EstadoPdb | null | undefined;
  return [
    { key: grupo + '_pdb', es: grupo + 'PDB', en: grupo + 'PDB', align: 'center', def: true,
      get: (r, l) => (e(r)?.confirmado ? L(l, 'Sí', 'Yes') : 'No') },
    { key: grupo + '_pdbPor', es: grupo + 'PDB · por', en: grupo + 'PDB · by', get: (r) => s(e(r)?.por) },
    { key: grupo + '_pdbEn', es: grupo + 'PDB · fecha y hora', en: grupo + 'PDB · date & time', mono: true,
      get: (r) => {
        const en = e(r)?.en;
        if (!en) return '';
        const d = new Date(en);
        const z = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())} ${z(d.getHours())}:${z(d.getMinutes())}`;
      } },
  ];
}

const orgCols: ColDef[] = [
  { key: 'OrgId', es: 'ID', en: 'ID', mono: true, def: true, get: (r) => s(r.OrgId) },
  { key: 'OrgName', es: 'Organización', en: 'Organization', def: true, get: (r) => s(r.OrgName) },
  { key: 'OrgCountry', es: 'País', en: 'Country', def: true, get: (r, l) => countryName(s(r.OrgCountry), l) },
  { key: 'RegisteredCity', es: 'Ciudad', en: 'City', get: (r) => s(r.RegisteredCity) },
  { key: 'YearJoined', es: 'Año ingreso', en: 'Year joined', mono: true, align: 'center', def: true, get: (r) => s(r.YearJoined) },
  { key: '_nSites', es: 'Sites', en: 'Sites', mono: true, align: 'center', def: true, get: (r) => Number(r._nSites) },
  { key: '_comp', es: '% Progreso', en: '% Progress', def: true, align: 'left', get: (r, l) => pctTxt(r._comp as Comp, l) },
  ...cuentas(true),
  { key: 'LastUpdated', es: 'Última actualización', en: 'Last updated', mono: true, def: true, get: (r) => fechaHoraOrdenable(r as { LastUpdated?: string; LastUpdatedAt?: string }) },
  ...pdbCols(''),
  { key: 'OrgWebAddress', es: 'Sitio web', en: 'Website', get: (r) => s(r.OrgWebAddress) },
  { key: '_legal', es: 'Contacto legal', en: 'Legal contact', get: (r) => fullName(s(r.LegalContactFirstName), s(r.LegalContactLastName)) },
  { key: 'LegalContactEmailAddress', es: 'Email legal', en: 'Legal email', get: (r) => s(r.LegalContactEmailAddress) },
  { key: 'BillingContactEmailAddress', es: 'Email facturación', en: 'Billing email', get: (r) => s(r.BillingContactEmailAddress) },
  { key: 'ContractCity', es: 'Ciudad contractual', en: 'Contract city', get: (r) => s(r.ContractCity) },

  /* Perfil del socio. Salen por defecto: son lo que el proceso de
     actualización existe para recoger, y un informe que no los enseñe no
     sirve para saber quién ha contestado.

     Se exportan con la ETIQUETA, no con el código. El informe lo lee una
     persona, y «aeco» no le dice nada. La importación acepta las dos formas,
     así que un archivo exportado se puede volver a subir sin tocarlo. */
  { key: 'EmployeeCount', es: 'Empleados', en: 'Employees', def: true, align: 'center', get: (r, l) => etiquetas('EmployeeCount', r.EmployeeCount, l) },
  { key: 'OrgIndustry', es: 'Industria', en: 'Industry', def: true, get: (r, l) => etiquetas('OrgIndustry', r.OrgIndustry, l) },
  { key: 'ProductPortfolio', es: 'Portafolio', en: 'Portfolio', def: true, get: (r, l) => etiquetas('ProductPortfolio', r.ProductPortfolio, l) },
  { key: 'ProductPortfolioOther', es: 'Portafolio · otro', en: 'Portfolio · other', get: (r) => s(r.ProductPortfolioOther) },
  { key: 'TrainingEnvironment', es: 'Entorno de formación', en: 'Training environment', def: true, get: (r, l) => etiquetas('TrainingEnvironment', r.TrainingEnvironment, l) },
  { key: 'OffersCertPrep', es: 'Prepara certificaciones', en: 'Delivers cert. prep', def: true, align: 'center', get: (r, l) => etiquetas('OffersCertPrep', r.OffersCertPrep, l) },
  { key: 'OffersEduTraining', es: 'Capacitación EDU', en: 'EDU training', def: true, align: 'center', get: (r, l) => etiquetas('OffersEduTraining', r.OffersEduTraining, l) },
  { key: 'InterestedEduTraining', es: 'Interés en sector EDU', en: 'Interest in EDU sector', def: true, align: 'center', get: (r, l) => etiquetas('InterestedEduTraining', r.InterestedEduTraining, l) },
];

const siteCols: ColDef[] = [
  { key: 'SiteId', es: 'ID', en: 'ID', mono: true, def: true, get: (r) => s(r.SiteId) },
  { key: 'SiteName', es: 'Site', en: 'Site', def: true, get: (r) => s(r.SiteName) },
  { key: 'OrgName', es: 'Organización', en: 'Organization', def: true, get: (r) => s(r.OrgName) },
  { key: 'OrgId', es: 'ID org.', en: 'Org ID', mono: true, get: (r) => s(r.OrgId) },
  { key: 'SiteCountry', es: 'País', en: 'Country', def: true, get: (r, l) => countryName(s(r.SiteCountry), l) },
  { key: 'SiteCity', es: 'Ciudad', en: 'City', get: (r) => s(r.SiteCity) },
  { key: 'ATCCSN', es: 'ATCCSN', en: 'ATCCSN', mono: true, get: (r) => s(r.ATCCSN) },
  { key: 'Workstations', es: 'Estaciones', en: 'Workstations', mono: true, align: 'center', def: true, get: (r) => s(r.Workstations) },
  { key: '_manager', es: 'Site Manager', en: 'Site Manager', def: true, get: (r) => fullName(s(r.SiteManagerFirstName), s(r.SiteManagerLastName)) },
  { key: '_director', es: 'ATC Director', en: 'ATC Director', get: (r) => fullName(s(r.ATCDirectorFirstName), s(r.ATCDirectorLastName)) },
  { key: '_primary', es: 'Primario', en: 'Primary', def: true, align: 'center', get: (r, l) => (r._primary ? L(l, 'Sí', 'Yes') : 'No') },
  { key: 'SitePostalCode', es: 'C.P.', en: 'Postal', mono: true, get: (r) => s(r.SitePostalCode) },
  { key: '_comp', es: '% Progreso', en: '% Progress', def: true, get: (r, l) => pctTxt(r._comp as Comp, l) },
  ...cuentas(true),
  { key: 'LastUpdated', es: 'Última actualización', en: 'Last updated', mono: true, def: true, get: (r) => fechaHoraOrdenable(r as { LastUpdated?: string; LastUpdatedAt?: string }) },
];

const instrCols: ColDef[] = [
  { key: 'InstructorId', es: 'ID', en: 'ID', mono: true, def: true, get: (r) => s(r.InstructorId) },
  { key: '_name', es: 'Instructor', en: 'Instructor', def: true, get: (r) => fullName(s(r.EnglishFirstName), s(r.EnglishLastName)) },
  { key: 'JobTitle', es: 'Cargo', en: 'Title', def: true, get: (r) => s(r.JobTitle) },
  { key: 'EmailAddress', es: 'Email', en: 'Email', def: true, get: (r) => s(r.EmailAddress) },
  { key: 'Mobile1', es: 'Móvil', en: 'Mobile', mono: true, get: (r) => s(r.Mobile1) },
  { key: 'Country', es: 'País', en: 'Country', def: true, get: (r, l) => countryName(s(r.Country), l) },
  { key: 'Certification', es: 'Certificación', en: 'Certification', def: true, get: (r) => s(r.Certification) },
  { key: 'AccessRoles', es: 'Roles de acceso', en: 'Access roles', get: (r) => s(r.AccessRoles) },
  { key: '_nAsg', es: 'Asignaciones', en: 'Assignments', mono: true, align: 'center', def: true, get: (r) => Number(r._nAsg) },
  { key: '_status', es: 'Estado', en: 'Status', def: true, align: 'left', get: (r, l) => (r._status === 'disassociated' ? L(l, 'Desasociado', 'Disassociated') : L(l, 'Activo', 'Active')) },
  { key: 'YearJoined', es: 'Año ingreso', en: 'Year joined', mono: true, align: 'center', get: (r) => s(r.YearJoined) },
  { key: '_comp', es: '% Progreso', en: '% Progress', def: true, get: (r, l) => pctTxt(r._comp as Comp, l) },
  ...cuentas(false),
  { key: 'LastUpdated', es: 'Última actualización', en: 'Last updated', mono: true, def: true, get: (r) => fechaHoraOrdenable(r as { LastUpdated?: string; LastUpdatedAt?: string }) },
];

/** The consolidated report exposes EVERY field of Organizations, Sites and
 *  Instructors in one cross-tab; the column picker decides what is shown. */
function buildConsolidatedCols(): ColDef[] {
  const mk = (prefix: string, schema: typeof orgSchema, group: string, defs: string[]): ColDef[] =>
    schema.map((fld) => ({
      key: prefix + fld.key,
      es: group + ' · ' + fld.label_es,
      en: group + ' · ' + fld.label_en,
      mono: fld.kind === 'mono',
      def: defs.includes(fld.key),
      get:
        fld.kind === 'country'
          ? (r: ReportRow, l: Lang) => countryName(s(r[prefix + fld.key]), l)
          : (r: ReportRow) => s(r[prefix + fld.key]),
    }));

  const oc = mk('org_', orgSchema, 'Org.', ['OrgId', 'OrgName', 'OrgCountry']);
  // org identifiers repeat on the site record — drop them so nothing duplicates
  const siteDrop = ['site_OrgId', 'site_OrgName', 'site_OrgWebAddress', 'site_PrimarySiteId'];
  const sc = mk('site_', siteSchema, 'Site', ['SiteId', 'SiteName', 'SiteCity']).filter((c) => !siteDrop.includes(c.key));
  const role: ColDef = { key: 'PrimaryRoleId', es: 'Rol en el site', en: 'Role at site', def: true, get: (r) => s(r.PrimaryRoleId) };
  const iname: ColDef = {
    key: '_iname',
    es: 'Instructor (nombre)',
    en: 'Instructor (name)',
    def: true,
    get: (r) => fullName(s(r.instr_EnglishFirstName), s(r.instr_EnglishLastName)),
  };
  const icols = mk('instr_', instructorSchema, 'Instr.', ['InstructorId']);
  const orgProg: ColDef[] = [
    { key: '_orgComp', es: 'Org. · % Progreso', en: 'Org. · % Progress', def: true,
      get: (r, l) => (r._orgComp ? pctTxt(r._orgComp as Comp, l) : '') },
    ...cuentas(false, '_orgComp', 'Org. · '),
    ...pdbCols('Org. · ').map((c) => ({ ...c, get: c.get, def: c.key === 'Org. · _pdb' })),
  ];
  const siteProg: ColDef[] = [
    { key: '_siteComp', es: 'Site · % Progreso', en: 'Site · % Progress', def: true,
      get: (r, l) => (r._siteComp ? pctTxt(r._siteComp as Comp, l) : '') },
    ...cuentas(false, '_siteComp', 'Site · '),
  ];
  return [...oc, ...orgProg, ...sc, ...siteProg, role, ...icols.slice(0, 1), iname, ...icols.slice(1)];
}

export const COLS: Record<Category, ColDef[]> = {
  orgs: orgCols,
  sites: siteCols,
  instructors: instrCols,
  consolidated: buildConsolidatedCols(),
};

export const CAT_META: Record<Category, { es: string; en: string; icon: IconElement; sheet: string }> = {
  orgs: { es: 'Organizaciones', en: 'Organizations', icon: ic.org, sheet: 'Organizaciones' },
  sites: { es: 'Sites', en: 'Sites', icon: ic.site, sheet: 'Sites' },
  instructors: { es: 'Instructores', en: 'Instructors', icon: ic.instr, sheet: 'Instructores' },
  consolidated: { es: 'Consolidado', en: 'Consolidated', icon: ic.table, sheet: 'Consolidado' },
};

/* ---------- filters ---------- */

export interface Filters {
  q: string;
  country: string;
  yearMin: string;
  yearMax: string;
  updatedFrom: string;
  updatedTo: string;
  completeness: 'all' | 'complete' | 'pending';
  status: string;
  certification: string;
  hasManager: 'all' | 'yes' | 'no';
  primary: 'all' | 'yes' | 'no';
  multiSite: 'all' | 'multi' | 'single';
  /** Segmentos: organizaciones, sites e instructores concretos. Vacío =
   *  todos. Se combinan con Y: «estos sites de estas organizaciones». */
  orgIds: string[];
  siteIds: string[];
  instructorIds: string[];
  /** Confirmado en la Partners Data Base. */
  pdb: 'all' | 'yes' | 'no';
  /** Rango de completitud, en %. */
  pctMin: string;
  pctMax: string;
}

export function defaultFilters(): Filters {
  return {
    q: '', country: '', yearMin: '', yearMax: '', updatedFrom: '', updatedTo: '',
    completeness: 'all', status: 'all', certification: '', hasManager: 'all', primary: 'all', multiSite: 'all',
    orgIds: [], siteIds: [], instructorIds: [], pdb: 'all', pctMin: '', pctMax: '',
  };
}

export type ColSelection = Record<Category, string[]>;

export function defaultCols(): ColSelection {
  const out = {} as ColSelection;
  CATS.forEach((c) => {
    out[c] = COLS[c].filter((x) => x.def).map((x) => x.key);
  });
  return out;
}

/** Lo que hace falta para saber si una fila cae en el segmento elegido:
 *  de qué organización es cada site, y en qué sites imparte cada
 *  instructor. */
function segmento(f: Filters) {
  const O = new Set(f.orgIds);
  const S = new Set(f.siteIds);
  const I = new Set(f.instructorIds);
  const activo = O.size > 0 || S.size > 0 || I.size > 0;
  const orgDeSite = new Map(ds.listSites().map((x) => [x.SiteId, x.OrgId]));
  const asg = ds.listAssignments();
  const sitesDeInstr = new Map<string, string[]>();
  const instrDeSite = new Map<string, string[]>();
  for (const a of asg) {
    (sitesDeInstr.get(a.InstructorId) ?? sitesDeInstr.set(a.InstructorId, []).get(a.InstructorId)!).push(a.SiteId);
    (instrDeSite.get(a.SiteId) ?? instrDeSite.set(a.SiteId, []).get(a.SiteId)!).push(a.InstructorId);
  }
  const sitesDeOrg = new Map<string, string[]>();
  for (const [sid, oid] of orgDeSite) (sitesDeOrg.get(oid) ?? sitesDeOrg.set(oid, []).get(oid)!).push(sid);

  /** ¿Un site entra? Su organización, él mismo y alguno de sus instructores. */
  const siteEntra = (sid: string) =>
    (!O.size || O.has(orgDeSite.get(sid) ?? '')) &&
    (!S.size || S.has(sid)) &&
    (!I.size || (instrDeSite.get(sid) ?? []).some((i) => I.has(i)));

  return {
    activo,
    org: (oid: string) =>
      !activo || ((!O.size || O.has(oid)) &&
        (!S.size && !I.size ? true : (sitesDeOrg.get(oid) ?? []).some(siteEntra))),
    site: (sid: string) => !activo || siteEntra(sid),
    instructor: (iid: string) =>
      !activo || ((!I.size || I.has(iid)) &&
        (!O.size && !S.size ? true : (sitesDeInstr.get(iid) ?? []).some((sid) =>
          (!O.size || O.has(orgDeSite.get(sid) ?? '')) && (!S.size || S.has(sid))))),
    fila: (oid: string, sid: string, iid: string) =>
      (!O.size || O.has(oid)) && (!S.size || S.has(sid)) && (!I.size || I.has(iid)),
  };
}

const enRango = (c: Comp, f: Filters) => {
  if (!f.pctMin && !f.pctMax) return true;
  if (c.sinDato) return false;
  if (f.pctMin && c.pct < +f.pctMin) return false;
  if (f.pctMax && c.pct > +f.pctMax) return false;
  return true;
};

export function applyFilters(category: Category, f: Filters): ReportRow[] {
  const seg = segmento(f);
  if (category === 'consolidated') {
    const q = f.q.trim().toLowerCase();
    return buildConsolidated().filter((r) => {
      if (
        q &&
        ![r.org_OrgId, r.org_OrgName, r.site_SiteId, r.site_SiteName, r.instr_InstructorId,
          fullName(s(r.instr_EnglishFirstName), s(r.instr_EnglishLastName)), r.instr_EmailAddress]
          .some((v) => v && String(v).toLowerCase().includes(q))
      ) return false;
      if (f.country && r.org_OrgCountry !== f.country && r.site_SiteCountry !== f.country) return false;
      if (f.yearMin && r.org_YearJoined && +s(r.org_YearJoined) < +f.yearMin) return false;
      if (f.yearMax && r.org_YearJoined && +s(r.org_YearJoined) > +f.yearMax) return false;
      if (f.updatedFrom && r.org_LastUpdated && s(r.org_LastUpdated) < f.updatedFrom) return false;
      if (f.updatedTo && r.org_LastUpdated && s(r.org_LastUpdated) > f.updatedTo) return false;
      if (f.status === 'with' && !r.instr_InstructorId) return false;
      if (f.status === 'without' && r.instr_InstructorId) return false;
      if (seg.activo && !seg.fila(s(r.org_OrgId), s(r.site_SiteId), s(r.instr_InstructorId))) return false;
      const pdb = (r._pdb as EstadoPdb | null | undefined)?.confirmado ?? false;
      if (f.pdb === 'yes' && !pdb) return false;
      if (f.pdb === 'no' && pdb) return false;
      if (!enRango(r._orgComp as Comp, f)) return false;
      return true;
    });
  }

  const list: Record<string, unknown>[] =
    category === 'orgs' ? (ds.listOrgs({ q: f.q }) as unknown as Record<string, unknown>[])
    : category === 'sites' ? (ds.listSites({ q: f.q }) as unknown as Record<string, unknown>[])
    : (ds.listInstructors({ q: f.q }) as unknown as Record<string, unknown>[]);

  const out: ReportRow[] = [];
  for (const row of list) {
    const id = s(category === 'orgs' ? row.OrgId : category === 'sites' ? row.SiteId : row.InstructorId);
    if (category === 'orgs' && !seg.org(id)) continue;
    if (category === 'sites' && !seg.site(id)) continue;
    if (category === 'instructors' && !seg.instructor(id)) continue;
    if (category === 'orgs' && f.pdb !== 'all') {
      const pdb = ds.getPdb(id)?.confirmado ?? false;
      if (f.pdb === 'yes' && !pdb) continue;
      if (f.pdb === 'no' && pdb) continue;
    }
    const cc = category === 'orgs' ? row.OrgCountry : category === 'sites' ? row.SiteCountry : row.Country;
    if (f.country && cc !== f.country) continue;
    const yr = s(row.YearJoined);
    if (f.yearMin && yr && +yr < +f.yearMin) continue;
    if (f.yearMax && yr && +yr > +f.yearMax) continue;
    if (f.updatedFrom && row.LastUpdated && s(row.LastUpdated) < f.updatedFrom) continue;
    if (f.updatedTo && row.LastUpdated && s(row.LastUpdated) > f.updatedTo) continue;

    const comp = recordCompleteness(category, row);
    if (f.completeness === 'complete' && comp.pending) continue;
    if (f.completeness === 'pending' && !comp.pending) continue;
    if (!enRango(comp, f)) continue;

    if (category === 'orgs' && f.multiSite !== 'all') {
      const n = ds.listSites({ orgId: s(row.OrgId) }).length;
      if (f.multiSite === 'multi' && n <= 1) continue;
      if (f.multiSite === 'single' && n > 1) continue;
    }
    if (category === 'sites') {
      const hasMgr = !!(row.SiteManagerFirstName && row.SiteManagerLastName);
      if (f.hasManager === 'yes' && !hasMgr) continue;
      if (f.hasManager === 'no' && hasMgr) continue;
      const isPrimary = row.SiteId === row.PrimarySiteId;
      if (f.primary === 'yes' && !isPrimary) continue;
      if (f.primary === 'no' && isPrimary) continue;
    }
    if (category === 'instructors') {
      const status = row.disassociated ? 'disassociated' : 'active';
      if (f.status !== 'all' && f.status !== status) continue;
      if (f.certification && row.Certification !== f.certification) continue;
    }

    // key on the record's OWN id — site and instructor rows also carry an
    // OrgId, which would collide across every site of the same organization
    const idKey = category === 'orgs' ? 'OrgId' : category === 'sites' ? 'SiteId' : 'InstructorId';
    const d: ReportRow = { ...row, _comp: comp, _key: s(row[idKey]) };
    if (category === 'orgs') {
      d._nSites = ds.listSites({ orgId: s(row.OrgId) }).length;
      d._pdb = ds.getPdb(s(row.OrgId));
    }
    if (category === 'sites') {
      d._primary = row.SiteId === row.PrimarySiteId;
      d._manager = !!(row.SiteManagerFirstName && row.SiteManagerLastName);
    }
    if (category === 'instructors') {
      d._nAsg = ds.listAssignments({ instructorId: s(row.InstructorId) }).length;
      d._status = row.disassociated ? 'disassociated' : 'active';
    }
    out.push(d);
  }
  return out;
}
